import AxiosOICStat from "../AxiosOICStat";
import { getLang } from "./countriesApi";

const CIF_BASE = "/cif";
const API_BASE = `${window.location.protocol}//${window.location.hostname}${import.meta.env.VITE_API_URL}`;

export interface CifCountrySummary {
  c_code: number;
  c_short_name_eng: string | null;
  c_full_name_eng: string | null;
  capital: string | null;
  flag: string | null;
  total_area: string | null;
  population: number | null;
  gdp_per_capita: number | null;
  currency_unit: string | null;
  region: string | null;
}

export interface CifAcronym {
  id: number;
  terminology: string | null;
  abbreviation: string | null;
  link: string | null;
}

// Fields hidden in the field config are left out of the response.
export interface CifBackground {
  official_name?: string | null;
  anthem?: string | null;
  anthem_in_official_language?: string | null;
  independence_date?: string | null;
  national_day?: string | null;
  capital?: string | null;
  other_cities?: string | null;
  official_languages?: string | null;
  historical_background?: string | null;
}

export interface CifGeography {
  location?: string | null;
  total_area?: string | null;
  total_land_area?: string | null;
  total_water_area?: string | null;
  land_boundaries?: string | null;
  border_countries?: string | null;
  coastline?: string | null;
  climate_and_weather?: string | null;
}

export interface CifDemography {
  nationality?: string | null;
  ethnic_groups?: string | null;
  religions?: string | null;
  human_development_index?: string | null;
  human_development_index_rank?: string | null;
  hdi_year?: string | null;
}

export interface CifEconomy {
  currency_unit?: string | null;
  export_partners?: string | null;
  import_partners?: string | null;
  oic_export_partners?: string | null;
  oic_import_partners?: string | null;
  natural_resources?: string | null;
  main_agricultural_products?: string | null;
  economy_overview?: string | null;
}

export interface CifPolitics {
  government_type?: string | null;
  chief_of_state?: string | null;
  name_of_parliament?: string | null;
}

export interface CifLogistics {
  time_difference?: string | null;
  country_code?: string | null;
  visa_issues?: string | null;
  electricity?: string | null;
  places_to_visit?: string | null;
  police_number?: string | null;
  ambulance_number?: string | null;
  fire_number?: string | null;
}

export type CifLinks = Record<string, string | null | undefined>;

export interface CifCabinetMember {
  c_code: number;
  title: string;
  name: string | null;
  website: string | null;
}

export interface CifKeyIndicator {
  indicator_id: number;
  indicator_name: string;
  year: number;
  value: number | null;
  unit: string | null;
}

export type CifKeyIndicatorKey =
  | "population_total"
  | "population_growth_rate"
  | "fertility_rate"
  | "population_density"
  | "adult_literacy_rate"
  | "exchange_rate"
  | "gdp_current_prices"
  | "gdp_per_capita"
  | "education_expenditure"
  | "health_expenditure"
  | "unemployment_rate"
  | "fdi_inflows"
  | "oda_net"
  | "merchandise_exports"
  | "merchandise_imports"
  | "intra_oic_exports"
  | "intra_oic_imports";

export interface CifProfile {
  c_code: number;
  background: CifBackground | null;
  geography: CifGeography | null;
  demography: CifDemography | null;
  economy: CifEconomy | null;
  politics: CifPolitics | null;
  logistics: CifLogistics | null;
  links: CifLinks | null;
  cabinet_members: CifCabinetMember[];
  key_indicators: Record<CifKeyIndicatorKey, CifKeyIndicator | null>;
}

export interface CifCategoryIndicator {
  indicator_id: number;
  indicator_name: string;
  year: number;
  value: string | null;
  unit: string | null;
}

export interface CifIndicatorCategory {
  cat_code: number;
  cat_name: string;
  indicators: CifCategoryIndicator[];
}

export interface CifAllIndicators {
  c_code: number;
  categories: CifIndicatorCategory[];
}

export interface CifTimeSeriesPoint {
  year: number;
  value: number | null;
}

export interface CifIndicatorTimeSeries {
  c_code: number;
  ind_code: number;
  indicator_name: string;
  unit: string | null;
  data: CifTimeSeriesPoint[];
}

export async function fetchCifSummary(lang: string = getLang()): Promise<CifCountrySummary[]> {
  const response = await AxiosOICStat.get<CifCountrySummary[]>(`${CIF_BASE}/summary`, { params: { lang } });
  return response.data;
}

export async function fetchCifAcronyms(lang: string = getLang()): Promise<CifAcronym[]> {
  const response = await AxiosOICStat.get<CifAcronym[]>(`${CIF_BASE}/acronyms`, { params: { lang } });
  return response.data;
}

export async function fetchCifProfile(cCode: number, lang: string = getLang()): Promise<CifProfile> {
  const response = await AxiosOICStat.get<CifProfile>(`${CIF_BASE}/${cCode}/profile`, { params: { lang } });
  return response.data;
}

export async function fetchCifSection(cCode: number, section: string, lang: string = getLang()) {
  const response = await AxiosOICStat.get(`${CIF_BASE}/${cCode}/${section}`, { params: { lang } });
  return response.data;
}

export async function fetchCifAllIndicators(cCode: number, lang: string = getLang()): Promise<CifAllIndicators> {
  const response = await AxiosOICStat.get<CifAllIndicators>(`${CIF_BASE}/${cCode}/all-indicators`, { params: { lang } });
  return response.data;
}

export async function fetchCifIndicatorTimeSeries(
  cCode: number,
  indCode: number,
  lang: string = getLang()
): Promise<CifIndicatorTimeSeries> {
  const response = await AxiosOICStat.get<CifIndicatorTimeSeries>(
    `${CIF_BASE}/${cCode}/indicator/${indCode}/timeseries`,
    { params: { lang } }
  );
  return response.data;
}

export async function downloadCifExportReport(cCode: number, lang: string = getLang()): Promise<Blob> {
  const response = await AxiosOICStat.get<Blob>(`${CIF_BASE}/${cCode}/export`, {
    params: { lang },
    responseType: "blob",
  });
  return response.data;
}

export function getAnthemUrl(cCode: number) {
  return `${API_BASE}cif/${cCode}/anthem`;
}
